import { useState, useEffect, createContext } from 'react';
import { useLoaderData } from "react-router-dom";
import cx from 'classnames';

import App from './App';
import Cover from './components/Cover';
import CustomCSS from "./components/CustomCSS";
import ErrorPage from './components/ErrorPage';
import Loader from './components/Loader';
import Vr from './components/Vr';
import { UnsavedChangesPrompt } from './components/EditTools/UnsavedChangesPrompt';

import {
  useAppDispatch,
  useAppSelector,
  apiLoadingStatusSelector,
  activeImageSelector,
  setActiveImage,
  mapNeedsSaveSelector,
  vrNeedsSaveSelector,
} from './store';

import './AppLoader.scss';

export const ComponentLoadedContext = createContext(
  {} as { componentLoaded:boolean }
);

export default function AppLoader() {

  const {
    isVr,
    layerId,
    cover,
    forceLoader:reallyForceLoader,
  } = useLoaderData() as {
    isVr:boolean,
    layerId:string,
    cover:string,
    forceLoader:boolean,
  };

  const dispatch = useAppDispatch();

  const apiResponse = useAppSelector(apiLoadingStatusSelector);
  const mapNeedsSave = useAppSelector(mapNeedsSaveSelector);
  const vrNeedsSave = useAppSelector(vrNeedsSaveSelector);
  const activeImage = useAppSelector(activeImageSelector);

  // Allows to process API response and set up store before loading App
  const [apiResponseProcessed, setApiResponseProcessed] = useState(false);
  // When it's false, Loader is not rendered
  const [showLoader, setShowLoader] = useState(true);
  // When it's true, shows Loader even if Map/Vr is loaded
  const [forceLoader, setForceLoader] = useState(true);
  // When it's true, Map/Vr is ready
  const [componentLoaded, setComponentLoaded] = useState(false);

  useEffect(() => {
    // Used on embed page to force showing Loader
    if (reallyForceLoader) return;
    // Used to delay hidding Loader if Map/Vr loads (too) fast
    const timer = setTimeout(() => setForceLoader(false), 1600);
    return () => clearTimeout(timer);
  }, [reallyForceLoader]);

  useEffect(() => {
    if (apiResponse !== 'success') return;
    const layerIdNum = parseInt(layerId);
    if (activeImage && !isNaN(layerIdNum) && activeImage.id !== layerIdNum) {
      dispatch(setActiveImage(layerIdNum));
    }
    setApiResponseProcessed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse]);

  if (apiResponse === 'failed') {
    return <ErrorPage />;
  }

  if (cover) {
    return (
      <Cover
        coverType={cover}
      />
    );
  }

  return (
    <ComponentLoadedContext.Provider value={{componentLoaded}}>
      <CustomCSS />
      {showLoader && (
        <div
          className={cx('loader', { 'fading': !forceLoader && componentLoaded })}
          onTransitionEnd={() => setShowLoader(false)}
        >
          <Loader />
        </div>
      )}
      {isVr && apiResponseProcessed && (
        <Vr
          setComponentLoaded={() => setComponentLoaded(true)}
        />
      )}
      {!isVr && apiResponseProcessed && (
        <App
          setComponentLoaded={() => setComponentLoaded(true)}
        />
      )}
      <UnsavedChangesPrompt hasUnsavedChanges={mapNeedsSave || vrNeedsSave} />
    </ComponentLoadedContext.Provider>
  );
}
